import { Controller, useFormContext } from "react-hook-form";
import { Link, Navigate, useParams } from "react-router-dom";

import { useTeam } from "~/contexts/TeamProvider";
import { UNLIMITED_PAGE_SCALE } from "~/helpers/consts";
import { routes } from "~/router";

import FormLabel from "../login/formLabel/FormLabel";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Switch from "../switch/Switch";
import RangeSlider from "../ui/rangeSlider/RangeSlider";
import Spinner from "../ui/spinner/Spinner";
import { SiteWithAuth } from "./General";
import { SiteSettingsFormValues } from "./SiteSettings";
import styles from "./SiteSettings.module.scss";

type AIEngineSettingsProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
  /** null when the plan has no page cap */
  pageMax: number | null;
};

const AIEngineSettings = ({
  siteData,
  isLoading,
  pageMax,
}: AIEngineSettingsProps) => {
  const { id } = useParams();

  if (!id) {
    throw new Error("AIEngineSettings: missing site id in params");
  }

  const { team } = useTeam();
  const canUpdateSite = siteData?.authorizations?.member
    ?.flat()
    .includes("update");

  const { control, watch, setValue } = useFormContext<SiteSettingsFormValues>();

  const metaSwitchEnabled = watch("ai_description");
  const aiTitle = watch("ai_title");
  const aiMetaOverride = watch("ai_meta_override");

  const isTitleDisabledByTeam = !team?.ai_title;
  const isDescriptionDisabledByTeam = !team?.ai_description;
  const effectiveAiTitle = isTitleDisabledByTeam ? false : aiTitle;
  const effectiveMetaSwitchEnabled = isDescriptionDisabledByTeam
    ? false
    : metaSwitchEnabled;

  const aiMetaOverrideLimitTitle = () => {
    if (aiMetaOverride === -1) {
      return "unlimited";
    }

    return aiMetaOverride;
  };

  const handleSliderChange =
    (field: "ai_meta_override") => (newValue: number) => {
      const value = newValue === 0 ? -1 : newValue;
      setValue(field, value, { shouldDirty: true });
    };

  if (team && !team.ai_engine_available) {
    return <Navigate to={routes.site.root({ id })} replace />;
  }

  if (isLoading) return <Spinner />;

  return (
    <div className={styles.settingsSection}>
      <p className={styles.settingsSectionText}>
        Allows you to configure AI engine settings for your project. Here, you
        can set the limits schemas and meta descriptions, and also enable or
        disable optimizations
      </p>

      <div className={styles.settingsSiteSpeedForm}>
        <div className={styles.settingsSwitchGroup}>
          <div>
            <FormLabel label="Generate or optimize too short page titles" />
            <p className={styles.settingsSectionText}>
              Improves page titles for better user attention and search engine
              rankings
            </p>
            {isTitleDisabledByTeam && (
              <div className={styles.settingsNotificationWrapper}>
                <NotificationBlock type="warning">
                  AI Titles are disabled, you can enable these features in{" "}
                  <Link to={"/settings/ai"}> Team settings</Link>.
                </NotificationBlock>
              </div>
            )}
          </div>
          <Controller
            name="ai_title"
            control={control}
            render={({ field }) => (
              <Switch
                disabled={!canUpdateSite || isTitleDisabledByTeam}
                checked={effectiveAiTitle}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div>
          <div className={styles.settingsSwitchGroup}>
            <div>
              <FormLabel label="Generate missing page meta description and titles" />
              <p className={styles.settingsSectionText}>
                Set the limits for the Meta Description and AI Title. Current
                limit {aiMetaOverrideLimitTitle()}
              </p>
              {isDescriptionDisabledByTeam && (
                <div className={styles.settingsNotificationWrapper}>
                  <NotificationBlock type="warning">
                    AI Description are disabled, you can enable these features
                    in <Link to={"/settings/ai"}> Team settings</Link>.
                  </NotificationBlock>
                </div>
              )}
            </div>
            <Controller
              name="ai_description"
              control={control}
              render={({ field }) => (
                <Switch
                  disabled={!canUpdateSite || isDescriptionDisabledByTeam}
                  checked={effectiveMetaSwitchEnabled}
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          {(effectiveMetaSwitchEnabled || effectiveAiTitle) && (
            <div className={styles.settingsSectionLeftGroup}>
              <div className={styles.settingsSliderContainer}>
                <RangeSlider
                  value={aiMetaOverride === -1 ? 0 : aiMetaOverride}
                  defaultValue={aiMetaOverride === -1 ? 0 : aiMetaOverride}
                  onChange={handleSliderChange("ai_meta_override")}
                  min={0}
                  max={pageMax ?? undefined}
                  scaleStops={
                    pageMax === null ? UNLIMITED_PAGE_SCALE : undefined
                  }
                  step={100}
                  disabled={!canUpdateSite || isDescriptionDisabledByTeam}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIEngineSettings;
